import React, { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { INR } from "@/lib/format";
import { PageHeader } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { BarChart3, Download, Loader2, Bookmark, Trash2, Star, Layers, TrendingDown } from "lucide-react";
import { toast } from "sonner";

const REPORTS = [
  { key: "leads", label: "Leads", path: "/leads" },
  { key: "customers", label: "Customers", path: "/customers" },
  { key: "policies", label: "Policies", path: "/policies" },
  { key: "commissions", label: "Commission MIS", path: "/commissions" },
  { key: "payments", label: "Payments", path: "/payments" },
  { key: "expenses", label: "Expenses", path: "/expenses" },
  { key: "loan-leads", label: "Loan Leads", path: "/loan/leads" },
  { key: "loan-applications", label: "Loan Applications", path: "/loan/applications" },
  { key: "loan-commissions", label: "Loan Commission MIS", path: "/loan/commissions" },
];
const REPORT_BY_KEY = Object.fromEntries(REPORTS.map((r) => [r.key, r]));

function toCSV(rows) {
  if (!rows.length) return "";
  const keys = Object.keys(rows[0]).filter((k) => typeof rows[0][k] !== "object");
  const head = keys.join(",");
  const body = rows.map((r) => keys.map((k) => `"${String(r[k] ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  return head + "\n" + body;
}
function download(csv, name) {
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

export default function Reports() {
  const { hasPerm } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [combined, setCombined] = useState(null);
  const [lost, setLost] = useState(null);
  const [lostTab, setLostTab] = useState("combined");
  const [busy, setBusy] = useState("");
  const [views, setViews] = useState([]);

  // saved-view builder
  const [svOpen, setSvOpen] = useState(false);
  const [svName, setSvName] = useState("");
  const [svReport, setSvReport] = useState("leads");
  const [svStatus, setSvStatus] = useState("");
  const [svShared, setSvShared] = useState(false);

  const loadViews = useCallback(() => { api.get("/report-views").then(({ data }) => setViews(data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/dashboard/metrics", { params: { period: "month" } }).then(({ data }) => setMetrics(data)).catch(() => {});
    api.get("/reports/combined").then(({ data }) => setCombined(data)).catch(() => {});
    api.get("/reports/lost-reasons").then(({ data }) => setLost(data)).catch(() => {});
    loadViews();
  }, [loadViews]);

  const exportReport = async (r, params = {}) => {
    const key = r.key + JSON.stringify(params);
    setBusy(key);
    try {
      const { data } = await api.get(r.path, { params: { limit: 1000, ...params } });
      const rows = data.items || [];
      if (!rows.length) { toast.info("No data to export"); return; }
      download(toCSV(rows), `omi-${r.key}-report.csv`);
      toast.success(`${r.label} exported (${rows.length} rows)`);
    } catch { toast.error("Export failed"); } finally { setBusy(""); }
  };

  const exportCombined = () => {
    if (!combined) return;
    const rows = [
      ["Metric", "Insurance", "Loan"],
      ["Total deals", combined.insurance.policies, combined.loan.applications],
      ["Business volume (INR)", combined.insurance.business_volume, combined.loan.business_volume],
      ["Converted", combined.insurance.converted, combined.loan.converted],
      ["Commission received (INR)", combined.insurance.commission_received, combined.loan.commission_received],
      ["Commission pending (INR)", combined.insurance.commission_pending, combined.loan.commission_pending],
      ["Lost / rejected", combined.insurance.lost, combined.loan.lost],
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    download(csv, "omi-combined-business-report.csv");
    toast.success("Combined report exported");
  };

  const exportLost = () => {
    const list = lost?.[lostTab] || [];
    if (!list.length) { toast.info("No lost deals recorded"); return; }
    const rows = [["Reason", "Count"], ...list.map((x) => [x.reason, x.count])];
    download(rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n"), `omi-lost-reasons-${lostTab}.csv`);
    toast.success("Lost reason report exported");
  };

  const saveView = async () => {
    if (!svName.trim()) { toast.error("Name your view"); return; }
    try {
      await api.post("/report-views", { name: svName.trim(), report_key: svReport, params: svStatus ? { status: svStatus } : {}, shared: svShared });
      toast.success("View saved"); setSvOpen(false); setSvName(""); setSvStatus(""); setSvShared(false); loadViews();
    } catch (e) { toast.error("Save failed"); }
  };
  const runView = (v) => {
    const r = REPORT_BY_KEY[v.report_key];
    if (!r) { toast.error("Report no longer available"); return; }
    exportReport(r, v.params || {});
  };
  const deleteView = async (v) => {
    try { await api.delete(`/report-views/${v.id}`); toast.success("View removed"); loadViews(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const lostList = lost?.[lostTab] || [];
  const maxLost = Math.max(1, ...lostList.map((x) => x.count));

  return (
    <div>
      <PageHeader title="Reports & MIS" subtitle="Financial reports distinguish actual, pending & expected amounts" icon={BarChart3} />

      {metrics && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="p-4"><div className="text-xs text-muted-foreground">Business Volume</div><div className="text-lg font-extrabold tabular mt-1">{INR(metrics.total_premium)}</div><div className="text-[10px] text-muted-foreground">premium, not revenue</div></Card>
          <Card className="p-4"><div className="text-xs text-muted-foreground">Commission Received</div><div className="text-lg font-extrabold tabular mt-1 text-emerald-600">{INR(metrics.commission_received)}</div><div className="text-[10px] text-muted-foreground">actual</div></Card>
          <Card className="p-4"><div className="text-xs text-muted-foreground">Expenses</div><div className="text-lg font-extrabold tabular mt-1 text-rose-600">{INR(metrics.expenses)}</div></Card>
          <Card className="p-4"><div className="text-xs text-muted-foreground">Net Profit</div><div className={`text-lg font-extrabold tabular mt-1 ${metrics.net_profit >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{INR(metrics.net_profit)}</div></Card>
        </div>
      )}

      {/* Combined Business Report */}
      {combined && (
        <Card className="p-5 mb-8" data-testid="combined-report-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold flex items-center gap-2"><Layers className="h-4 w-4 text-primary" /> Combined Business Report</h3>
            <Button variant="outline" size="sm" onClick={exportCombined} data-testid="export-combined-btn"><Download className="h-4 w-4 mr-1.5" />Export</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-xs text-muted-foreground"><th className="text-left py-2 font-medium">Metric</th><th className="text-right py-2 font-medium">Insurance</th><th className="text-right py-2 font-medium">Loan</th></tr></thead>
              <tbody>
                {[
                  ["Total deals", combined.insurance.policies, combined.loan.applications, false],
                  ["Business volume", INR(combined.insurance.business_volume), INR(combined.loan.business_volume), true],
                  ["Converted", combined.insurance.converted, combined.loan.converted, false],
                  ["Commission received", INR(combined.insurance.commission_received), INR(combined.loan.commission_received), true],
                  ["Commission pending", INR(combined.insurance.commission_pending), INR(combined.loan.commission_pending), true],
                  ["Lost / rejected", combined.insurance.lost, combined.loan.lost, false],
                ].map(([label, ins, loan]) => (
                  <tr key={label} className="border-b border-border/50"><td className="py-2.5">{label}</td><td className="py-2.5 text-right tabular font-semibold">{ins}</td><td className="py-2.5 text-right tabular font-semibold">{loan}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Lost Reason Report */}
      {lost && (
        <Card className="p-5 mb-8" data-testid="lost-reason-card">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h3 className="font-bold flex items-center gap-2"><TrendingDown className="h-4 w-4 text-rose-600" /> Lost Reason Analysis</h3>
            <div className="flex items-center gap-2">
              <div className="flex rounded-lg border border-border overflow-hidden text-xs">
                {[["combined", "Combined"], ["loan", "Loan"], ["renewal", "Renewal"]].map(([k, l]) => (
                  <button key={k} onClick={() => setLostTab(k)} className={`px-3 py-1.5 ${lostTab === k ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid={`lost-tab-${k}`}>{l}</button>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={exportLost} data-testid="export-lost-btn"><Download className="h-4 w-4 mr-1.5" />Export</Button>
            </div>
          </div>
          {lostList.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">No lost deals recorded for this segment.</p> : (
            <div className="space-y-2.5">
              {lostList.map((x, i) => (
                <div key={x.reason} className="flex items-center gap-3" data-testid="lost-reason-row">
                  <span className="w-5 text-xs text-muted-foreground tabular">{i + 1}</span>
                  <span className="w-48 text-sm truncate" title={x.reason}>{x.reason}</span>
                  <div className="flex-1 h-5 rounded bg-muted overflow-hidden"><div className="h-full bg-rose-500/80 rounded flex items-center justify-end pr-2" style={{ width: `${Math.max(8, (x.count / maxLost) * 100)}%` }}><span className="text-[10px] font-bold text-white tabular">{x.count}</span></div></div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Saved Report Views */}
      <Card className="p-5 mb-8" data-testid="saved-views-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold flex items-center gap-2"><Bookmark className="h-4 w-4 text-primary" /> Saved Report Views</h3>
          <Button variant="outline" size="sm" onClick={() => setSvOpen(true)} data-testid="new-view-btn"><Bookmark className="h-4 w-4 mr-1.5" />Save a view</Button>
        </div>
        {views.length === 0 ? <p className="text-sm text-muted-foreground">No saved views yet. Save your favourite report + filter to reopen in one click.</p> : (
          <div className="flex flex-wrap gap-2">
            {views.map((v) => (
              <div key={v.id} className="flex items-center gap-1.5 rounded-full border border-border bg-muted/40 pl-3 pr-1.5 py-1" data-testid="saved-view-chip">
                <button onClick={() => runView(v)} className="flex items-center gap-1.5 text-sm font-medium hover:text-primary" data-testid="run-view-btn">
                  {v.shared && <Star className="h-3 w-3 text-amber-500" />}
                  {v.name}
                  <span className="text-[10px] text-muted-foreground">{REPORT_BY_KEY[v.report_key]?.label || v.report_key}{v.params?.status ? ` · ${v.params.status}` : ""}</span>
                </button>
                {v.owned && <button onClick={() => deleteView(v)} className="text-muted-foreground hover:text-rose-600 p-0.5" data-testid="delete-view-btn"><Trash2 className="h-3.5 w-3.5" /></button>}
              </div>
            ))}
          </div>
        )}
      </Card>

      <h3 className="font-bold mb-4">Downloadable Reports</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {REPORTS.map((r) => (
          <Card key={r.key} className="p-5 flex items-center justify-between">
            <div><div className="font-semibold">{r.label}</div><div className="text-xs text-muted-foreground">CSV export</div></div>
            <Button variant="outline" size="sm" onClick={() => exportReport(r)} disabled={busy === r.key + "{}"} data-testid={`export-${r.key}-btn`}>
              {busy === r.key + "{}" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-1.5" />Export</>}
            </Button>
          </Card>
        ))}
      </div>

      {/* Save view dialog */}
      <Dialog open={svOpen} onOpenChange={setSvOpen}>
        <DialogContent data-testid="save-view-dialog">
          <DialogHeader><DialogTitle>Save a report view</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs text-muted-foreground">View name</label><Input value={svName} onChange={(e) => setSvName(e.target.value)} placeholder="e.g. My pending loan leads" data-testid="view-name-input" /></div>
            <div><label className="text-xs text-muted-foreground">Report</label>
              <Select value={svReport} onValueChange={setSvReport}><SelectTrigger data-testid="view-report-select"><SelectValue /></SelectTrigger>
                <SelectContent>{REPORTS.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><label className="text-xs text-muted-foreground">Status filter (optional)</label><Input value={svStatus} onChange={(e) => setSvStatus(e.target.value)} placeholder="e.g. new, approved, pending" data-testid="view-status-input" /></div>
            <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={svShared} onCheckedChange={setSvShared} data-testid="view-shared-checkbox" /> Share with everyone</label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSvOpen(false)}>Cancel</Button>
            <Button onClick={saveView} data-testid="save-view-confirm">Save View</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
